
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect } from "react";

interface Entity {
  id: string;
  name: string;
}

interface ExpenseFormProps {
  entities: Array<Entity>;
  selectedEntity: string;
  amount: string;
  category: string;
  description: string;
  expenseDate: string;
  onEntityChange: (value: string) => void;
  onAmountChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onExpenseDateChange: (value: string) => void;
  entityType?: 'house' | 'plot';
  defaultEntity?: string;
}

export const ExpenseForm = ({
  entities,
  selectedEntity,
  amount,
  category,
  description,
  expenseDate,
  onEntityChange,
  onAmountChange,
  onCategoryChange,
  onDescriptionChange,
  onExpenseDateChange,
  entityType = 'house',
  defaultEntity,
}: ExpenseFormProps) => {
  // Fetch categories from the database
  const { data: categories = [] } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('expense_categories')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error fetching categories:', error);
        throw error;
      }

      return data;
    },
  });

  // Set default entity if provided
  useEffect(() => {
    if (defaultEntity && !selectedEntity) {
      onEntityChange(defaultEntity);
    }
  }, [defaultEntity, selectedEntity, onEntityChange]);

  return (
    <div className="grid gap-4 py-4">
      {(!defaultEntity || entities.length > 1) && (
        <div className="grid gap-2">
          <Label htmlFor="entity" className="text-base">
            {entityType === 'house' ? 'Casa' : 'Parcela'}
          </Label>
          <Select value={selectedEntity} onValueChange={onEntityChange}>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder={`Seleccione ${entityType === 'house' ? 'casa' : 'parcela'}`} />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {entities.map((entity) => (
                <SelectItem key={entity.id} value={entity.id}>
                  {entity.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid gap-2">
        <Label htmlFor="amount" className="text-base">Monto</Label>
        <Input
          id="amount"
          type="number"
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          placeholder="Ingrese monto"
          className="text-base"
          required
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="category" className="text-base">Categoría</Label>
        <Select value={category} onValueChange={onCategoryChange} required>
          <SelectTrigger className="bg-white">
            <SelectValue placeholder="Seleccione categoría" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="expenseDate" className="text-base">Fecha</Label>
        <Input
          id="expenseDate"
          type="date"
          value={expenseDate}
          onChange={(e) => onExpenseDateChange(e.target.value)}
          className="text-base"
          required
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="description" className="text-base">Descripción</Label>
        <Input
          id="description"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder="Ingrese descripción"
          className="text-base"
          required
        />
      </div>
    </div>
  );
};
